/**
 * ConsolidatedAuditTrail Component
 * Displays a unified view of all notes, remarks, and activity history for a site
 */

import React, { useState, useMemo } from 'react';
import {
  SITE_HISTORY_CHANGE_TYPE,
  SITE_HISTORY_CHANGE_TYPE_LABELS
} from '../../utils/constants';

// Category configuration
const CATEGORIES = {
  all: { label: 'All', color: 'gray' },
  phases: { label: 'Phases', color: 'blue' },
  tasks: { label: 'Tasks', color: 'teal' },
  status: { label: 'Status', color: 'purple' },
  attendance: { label: 'Attendance', color: 'green' },
  notes: { label: 'Notes', color: 'amber' },
  milestone: { label: 'Milestone', color: 'indigo' }
};

// Get icon for entry type
const getEntryIcon = (type, category) => {
  const icons = {
    phase_started: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      </svg>
    ),
    phase_completed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    phase_skipped: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      </svg>
    ),
    phase_progress_updated: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      </svg>
    ),
    task_created: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    task_completed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    status_changed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    attendance_check_in: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
      </svg>
    ),
    attendance_check_out: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
    ),
    notes_updated: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    contract_signed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    site_created: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )
  };

  if (category === 'notes') {
    return icons.notes_updated;
  }

  return icons[type] || (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

// Get color classes for category
const getCategoryColor = (category) => {
  const colors = {
    phases: 'bg-blue-100 text-blue-600',
    tasks: 'bg-teal-100 text-teal-600',
    status: 'bg-purple-100 text-purple-600',
    attendance: 'bg-green-100 text-green-600',
    notes: 'bg-amber-100 text-amber-600',
    milestone: 'bg-indigo-100 text-indigo-600'
  };
  return colors[category] || 'bg-gray-100 text-gray-600';
};

// Normalize history entries
const normalizeHistoryEntry = (item) => {
  const categoryMap = {
    [SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED]: 'phases',
    [SITE_HISTORY_CHANGE_TYPE.TASK_CREATED]: 'tasks',
    [SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED]: 'tasks',
    [SITE_HISTORY_CHANGE_TYPE.TASK_DELETED]: 'tasks',
    [SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED]: 'status',
    [SITE_HISTORY_CHANGE_TYPE.NOTES_UPDATED]: 'notes',
    [SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_IN]: 'attendance',
    [SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_OUT]: 'attendance'
  };

  return {
    id: item.id,
    type: 'history',
    changeType: item.changeType,
    category: categoryMap[item.changeType] || 'status',
    timestamp: item.timestamp || item.createdAt,
    user: item.changedByUser,
    title: SITE_HISTORY_CHANGE_TYPE_LABELS[item.changeType] || item.changeType,
    description: item.description,
    notes: item.metadata?.notes || item.metadata?.reason || null,
    metadata: item.metadata
  };
};

// Normalize attendance entries
const normalizeAttendanceEntry = (item) => ({
  id: `attendance-${item.id}`,
  type: 'attendance',
  changeType: item.type === 'check_in' ? 'attendance_check_in' : 'attendance_check_out',
  category: 'attendance',
  timestamp: item.recordedAt,
  user: item.user,
  title: item.type === 'check_in' ? 'Check-In' : 'Check-Out',
  description: `${item.user?.nickname || item.user?.fullName || 'User'} ${item.type === 'check_in' ? 'checked in' : 'checked out'}`,
  notes: item.notes || null,
  metadata: {
    type: item.type,
    location: item.latitude && item.longitude ? `${item.latitude}, ${item.longitude}` : null
  }
});

// Format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

const ConsolidatedAuditTrail = ({
  history = [],
  attendance = [],
  site = null,
  loading = false,
  onAddNote,
  onEditNote
}) => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotesOnly, setShowNotesOnly] = useState(false);

  // Normalize and combine all entries
  const allEntries = useMemo(() => {
    const entries = [];

    // Add history entries
    history.forEach(item => {
      entries.push(normalizeHistoryEntry(item));
    });

    // Add attendance entries (only those with notes if showNotesOnly is true)
    attendance.forEach(item => {
      if (!showNotesOnly || item.notes) {
        entries.push(normalizeAttendanceEntry(item));
      }
    });

    // Add milestone entries from site data
    if (site) {
      // Contract Signed Date
      if (site.contractSignedDate) {
        entries.push({
          id: 'milestone-contract-signed',
          type: 'milestone',
          changeType: 'contract_signed',
          category: 'milestone',
          timestamp: site.contractSignedDate,
          user: null,
          title: 'Contract Signed',
          description: `Contract signed${site.signingVenue ? ` at ${site.signingVenue}` : ''}`,
          notes: null,
          metadata: { signingVenue: site.signingVenue }
        });
      }

      // Site Database Created
      if (site.createdAt) {
        entries.push({
          id: 'milestone-site-created',
          type: 'milestone',
          changeType: 'site_created',
          category: 'milestone',
          timestamp: site.createdAt,
          user: null,
          title: 'Site Database Created',
          description: 'Site added to database',
          notes: null,
          metadata: null
        });
      }
    }

    // Sort by timestamp (most recent first)
    return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [history, attendance, showNotesOnly, site]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let filtered = allEntries;

    // Category filter
    if (filter !== 'all') {
      filtered = filtered.filter(entry => entry.category === filter);
    }

    // Notes only filter
    if (showNotesOnly) {
      filtered = filtered.filter(entry => entry.notes);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.description?.toLowerCase().includes(query) ||
        entry.notes?.toLowerCase().includes(query) ||
        entry.title?.toLowerCase().includes(query) ||
        entry.user?.nickname?.toLowerCase().includes(query) ||
        entry.user?.fullName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allEntries, filter, showNotesOnly, searchQuery]);

  // Count notes for badge
  const notesCount = useMemo(() =>
    allEntries.filter(entry => entry.notes).length,
    [allEntries]
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with Add Note button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit Trail</h3>
          <p className="text-sm text-gray-500">{filteredEntries.length} entries</p>
        </div>
        {onAddNote && (
          <button
            onClick={onAddNote}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Note
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search entries..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
        {Object.entries(CATEGORIES)
          .filter(([key]) => key !== 'milestone') // Milestones always show in "all", no separate tab
          .map(([key, { label }]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`flex-1 min-w-[60px] px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Notes Only Toggle */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showNotesOnly}
            onChange={(e) => setShowNotesOnly(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Show notes/remarks only</span>
        </label>
        {notesCount > 0 && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {notesCount} with notes
          </span>
        )}
      </div>

      {/* Site-Level Notes Section */}
      {site && (site.handoverNotes || site.internalNotes || site.clientFeedback) && filter === 'all' && !searchQuery && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-amber-800">Site Notes</h4>
            {onEditNote && (
              <button
                onClick={onEditNote}
                className="text-xs text-amber-600 hover:text-amber-700 font-medium"
              >
                Edit
              </button>
            )}
          </div>
          <div className="space-y-3">
            {site.handoverNotes && (
              <div>
                <span className="text-xs font-medium text-amber-700 uppercase">Handover</span>
                <p className="text-sm text-gray-700 mt-0.5">{site.handoverNotes}</p>
              </div>
            )}
            {site.internalNotes && (
              <div>
                <span className="text-xs font-medium text-amber-700 uppercase">Internal</span>
                <p className="text-sm text-gray-700 mt-0.5">{site.internalNotes}</p>
              </div>
            )}
            {site.clientFeedback && (
              <div>
                <span className="text-xs font-medium text-amber-700 uppercase">Client Feedback</span>
                <p className="text-sm text-gray-700 mt-0.5">{site.clientFeedback}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto">
        {filteredEntries.length > 0 ? (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {/* Timeline items */}
            <div className="space-y-4">
              {filteredEntries.map((entry, index) => (
                <div key={entry.id || index} className="relative flex gap-4">
                  {/* Icon */}
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getCategoryColor(entry.category)}`}>
                    {getEntryIcon(entry.changeType, entry.category)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pb-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(entry.category)}`}>
                            {CATEGORIES[entry.category]?.label || entry.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 mt-1">
                          {entry.description || entry.title}
                        </p>
                        {entry.notes && (
                          <div className="mt-2 p-2 bg-gray-50 border-l-2 border-gray-300 rounded-r">
                            <p className="text-xs text-gray-600 italic">
                              "{entry.notes}"
                            </p>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>
                    {/* User info */}
                    {entry.user && (
                      <p className="text-xs text-gray-400 mt-1">
                        by {entry.user.nickname || entry.user.fullName || 'Unknown'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-gray-500">
              {searchQuery ? 'No matching entries found' : 'No audit trail entries yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsolidatedAuditTrail;
